import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { MetricHistoryDataPoint } from '../hooks/useMetricHistory.ts';

interface TooltipPayload {
  payload: { timestamp: number; value: number; formattedDate: string };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  valueFormatter?: (value: number) => string;
}

function CustomTooltip({ active, payload, valueFormatter }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  const formatted = valueFormatter ? valueFormatter(data.value) : data.value.toLocaleString();
  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='text-sm text-muted-foreground mb-1'>
        {new Date(data.timestamp).toLocaleString()}
      </p>
      <p className='font-semibold'>Value: {formatted}</p>
    </div>
  );
}

export type MetricsYDomainMode = 'zero' | 'tight';

export interface MetricsChartProps {
  dataPoints: MetricHistoryDataPoint[];
  isLoading?: boolean;
  error?: Error | null;
  height?: number;
  /**
   * How to pick the y-axis domain:
   * - `'tight'` (default): fit to data with padding scaled so 1% wiggles
   *   don't dominate AND so series that hover near a stable value remain
   *   legible. The floor never drops below 0 for non-negative data.
   * - `'zero'`: floor at 0, ceil 5% above max. Opt in when the honest
   *   zero baseline matters more than visible movement.
   */
  yDomainMode?: MetricsYDomainMode;
  /**
   * Reference values drawn as dashed horizontal lines: `baseline` (where the
   * series started, e.g. Jan-2026 monthly spend) and `target` (the goal, e.g.
   * baseline × 0.75). Must be on the SAME basis as the plotted series — never
   * draw a monthly line onto a daily axis. Both are folded into the y-axis
   * domain so a line outside the data range isn't clipped off the chart.
   */
  baseline?: number;
  target?: number;
  /** Formats values in the tooltip (e.g. currency). Default: toLocaleString. */
  valueFormatter?: (value: number) => string;
}

/**
 * Compute a y-axis domain for the line chart.
 *
 * Default ('tight'): fit to the data window with padding that's the largest
 * of half the range, 0.5% of the max magnitude, or 1 unit. This keeps
 * stable-near-a-value series (SLAs at 99.x%, steady user counts) legible
 * without re-introducing lie-factor exaggeration when values are nearly
 * identical. Non-negative data never gets a negative floor.
 *
 * 'zero': zero baseline with 5% top headroom, for when the honest zero
 * baseline matters more than visible movement.
 *
 * Degenerate cases (empty series, all-zero series) get sensible defaults.
 *
 * `referenceValues` (e.g. a target line) participate in the min/max so a
 * reference outside the data range stays visible instead of being clipped;
 * they don't count as data otherwise (an empty series still returns the
 * default domain).
 */
export function computeMetricsYDomain(
  values: number[],
  mode: MetricsYDomainMode = 'tight',
  referenceValues: number[] = [],
): [number, number] {
  if (values.length === 0) return [0, 100];
  const all = [...values, ...referenceValues];
  const min = Math.min(...all);
  const max = Math.max(...all);

  if (mode === 'tight') {
    const range = max - min;
    const pad = Math.max(range * 0.5, Math.abs(max) * 0.005, 1);
    const floor = Math.floor(min - pad);
    const ceil = Math.ceil(max + pad);
    // Don't drop below zero if the data is non-negative — easier to read.
    return [min >= 0 ? Math.max(0, floor) : floor, ceil];
  }

  const floor = Math.min(0, Math.floor(min));
  const ceil = max > 0 ? Math.ceil(max * 1.05) : Math.max(1, Math.ceil(max));
  return [floor, ceil];
}

export function MetricsChart({
  dataPoints,
  isLoading = false,
  error = null,
  height = 240,
  yDomainMode = 'tight',
  baseline,
  target,
  valueFormatter,
}: MetricsChartProps) {
  const chartData = useMemo(() => {
    return dataPoints.map((point) => {
      const date = new Date(point.timestamp);
      return {
        timestamp: point.timestamp,
        value: point.value,
        formattedDate: date.toLocaleDateString([], { month: 'short', day: 'numeric' }),
      };
    });
  }, [dataPoints]);

  const yAxisDomain = useMemo<[number, number]>(
    () =>
      computeMetricsYDomain(
        chartData.map((d) => d.value),
        yDomainMode,
        [baseline, target].filter((v): v is number => v !== undefined),
      ),
    [chartData, yDomainMode, baseline, target],
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center' style={{ height }}>
        <p className='text-sm text-muted-foreground'>Loading chart data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center' style={{ height }}>
        <p className='text-sm text-destructive'>Failed to load chart data</p>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center text-center' style={{ height }}>
        <p className='text-sm text-muted-foreground'>No data available yet</p>
        <p className='text-xs text-muted-foreground mt-1'>
          Metrics are recorded on a schedule. Check back soon.
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={height}>
      <LineChart data={chartData}>
        <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
        <XAxis dataKey='formattedDate' tick={{ fontSize: 11 }} interval='preserveStartEnd' />
        <YAxis domain={yAxisDomain} tick={{ fontSize: 11 }} width={50} />
        <Tooltip content={<CustomTooltip valueFormatter={valueFormatter} />} />
        {baseline !== undefined && (
          <ReferenceLine
            y={baseline}
            stroke='hsl(var(--muted-foreground))'
            strokeDasharray='6 4'
            label={{
              value: 'Baseline',
              position: 'insideTopRight',
              fontSize: 10,
              fill: 'hsl(var(--muted-foreground))',
            }}
          />
        )}
        {target !== undefined && (
          <ReferenceLine
            y={target}
            stroke='hsl(var(--primary))'
            strokeDasharray='6 4'
            label={{
              value: 'Target',
              position: 'insideBottomRight',
              fontSize: 10,
              fill: 'hsl(var(--primary))',
            }}
          />
        )}
        <Line
          type='linear'
          dataKey='value'
          stroke='hsl(var(--primary))'
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
